"use client";
import { useRightDrawer } from "@/contexts/RightDrawerContext";
import { Colors } from "@/enums/Colors";
import { AppContainerV2, AppContainerV2Props } from "@/external/ensolvers-core-frontend-mui-v5/components/Main/AppContainerV2";
import { useResponsive } from "@/hooks/useResponsive";
import { useToggle } from "@/hooks/useToggle";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from "@mui/material/styles";
import DefaultAppBar from "./app-bar-components/DefaultAppBar";
import { Sidebar } from "./layout/Sidebar";
import { BottomNavigation } from "./navigation/BottomNavigation";

const APPBAR_FIXED_HEIGHT = "64px";

type LazoAppContainerProps = Omit<AppContainerV2Props, "rightContainer" | "AppBar" | "Drawer" | "MobileDrawer"> & {
  rightContainer: React.ReactNode;
  renderDefaultAppBar: boolean;
  drawerItems: any[];
  footer?: React.ReactNode | ((open: boolean) => React.ReactNode);
  open?: boolean;
  onToggleOpen?: () => void;
  mobileOpen?: boolean;
  onToggleMobileOpen?: () => void;
  drawerHeader?: React.ReactNode | ((open: boolean) => React.ReactNode);
  navbar?: React.ReactNode;
};

AppContainerV2.defaultProps = {
  drawerWidth: 240,
  containerBackgroundColor: "white",
  hideTopSpacer: false,
};

export function LazoAppContainer(props: LazoAppContainerProps) {
  const [internalOpen, toggleInternalOpen] = useToggle(true);
  const [internalMobileOpen, toggleInternalMobileOpen] = useToggle(false);

  // Controlled or uncontrolled
  const open = props.open !== undefined ? props.open : internalOpen;
  const toggleOpen = props.onToggleOpen || toggleInternalOpen;

  const mobileOpen = props.mobileOpen !== undefined ? props.mobileOpen : internalMobileOpen;
  const toggleMobileOpen = props.onToggleMobileOpen || toggleInternalMobileOpen;

  const { isOpen, isOnAnotherPage } = useRightDrawer();
  const showChat = isOpen && !isOnAnotherPage;
  const { isLargeScreen } = useResponsive();
  const chatWidth = !isLargeScreen ? "45%" : "35%";

  return (
    <Viewport>
      <Sidebar
        open={open}
        onClose={toggleOpen}
        onToggle={toggleOpen}
        width={props.drawerWidth}
        mobileOpen={mobileOpen}
        onMobileClose={toggleMobileOpen}
        items={props.drawerItems}
        footer={props.footer}
        header={props.drawerHeader}
      />

      <MainColumn>
        {/* Top Navigation Area */}
        <NavbarWrapper>
          <CssBaseline />
          {props.navbar}
          {props.renderDefaultAppBar && (
            <DefaultAppBar
              open={open}
              clippedDrawer={props.clippedDrawer}
              onClickMenuButton={toggleOpen}
              onClickMobileMenuButton={toggleMobileOpen}
              drawerWidth={props.drawerWidth}
              breadcrumbs={""}
            />
          )}
        </NavbarWrapper>

        {/* Content & Right Sidebar Area */}
        <ContentRow>
          {/* Central Scrollable Page Content */}
          <PageScrollArea id="page-scroll-area">{props.children}</PageScrollArea>

          {/* Right Sidebar (Chat) */}
          {props.rightContainer && (
            <RightSidebarWrapper showChat={showChat} chatWidth={chatWidth}>
              {props.rightContainer}
            </RightSidebarWrapper>
          )}
        </ContentRow>
        <BottomNavigation onClickMore={toggleMobileOpen} />
      </MainColumn>
    </Viewport>
  );
}

// -- Styled Components for Holy Grail Layout --

const Viewport = styled(Box)({
  display: "flex",
  flexDirection: "row",
  height: "100vh",
  width: "100vw",
  overflow: "hidden", // Prevent body scroll
  backgroundColor: Colors.LIGHT_GRAY_F7F8FA, // Main background
});

const MainColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  overflow: "hidden", // Contain children
  position: "relative",
});

const NavbarWrapper = styled(Box)({
  flexShrink: 0, // Never shrink, always full height required
  zIndex: 10,
  backgroundColor: "transparent", // Navbar component handles its own bg/glass
});

const ContentRow = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flex: 1,
  overflow: "hidden", // Contain scroll area
});

const PageScrollArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0, // Force respect flex boundaries (prevent pushing chat off-screen)
  overflowY: "auto", // The ONLY vertical scrollbar in the app
  overflowX: "hidden", // Prevent horizontal overflow triggers (content should adapt)
  scrollbarGutter: "stable", // Reserve scrollbar space to prevent layout shift
  position: "relative",
  padding: "0",
  [theme.breakpoints.up("sm")]: {
    padding: "0 0.5rem 0.5rem 0.5rem", // Give some breathing room if desired
  },
}));

const RightSidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showChat" && prop !== "chatWidth",
})<{ showChat?: boolean; chatWidth?: string }>(({ showChat, chatWidth }) => ({
  display: "flex",
  width: showChat ? chatWidth : 0,
  flexShrink: 0,
  height: "100%",
  position: "relative",
  overflowY: "auto",
  transition: "width 0.3s ease",
}));
